import React, { useCallback, useMemo, useState, useRef } from 'react';
import {
  LeftSection,
  TopSection,
  SubHeading,
  TitleSection,
  Heading,
  OuterDiv,
  Content,
  Form,
  Label,
  ItemDiv,
  ButtonSection,
  NotFound
} from '../projectDetails/styles';
import { Dropdown, DropdownItem } from '../../../../../component/dropdown';
import SVGIcon from '../../../../../assets/images/svg/SVGIcon';
import Button from '../../../../../component/button';
import { rootStore } from '../../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { isEmpty } from '../../../../../helpers/common';
import { CreateProjectPayloadInterfaceV2 } from '../../../../../interfaces/ProjectInterface';
import { createNewProject, dispatchProjectSuccess } from '../../../../../services/projectServices';
import { useHistory } from 'react-router-dom';
import { PROJECT_ANALYTICS } from '../../../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../../../services/analyticsService';
import { BUTTON_TYPE, ICON_SIZE, PROJECT_ICON_TYPE } from '../../../../../global/constants';
import { useMobileDevice } from '../../../../../global/useMobile';
import Input from '../../../../../component/input';
import { ROUTES } from '../../../../../routes/urls';

interface Props {}

function TeamMembers(props: Props) {
  //State variables
  const [search, setSearch] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  //Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const membersDropdownRef = useRef<{
    closeDropdown: () => void;
  }>(null);
  //Mobx store variables
  const { workspaceStore, projectStore, currentUser } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { createProjectInput } = projectStore;
  //Other variables
  const history = useHistory();
  const isMobile = useMobileDevice();

  const scrollToItem = useCallback((index: number) => {
    if (itemRefs.current[index]) {
      itemRefs.current[index]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, []);

  //handle Add members
  const handleProjectMember = useCallback(
    (userId: string) => {
      if (createProjectInput?.hasProjectUser(userId)) {
        createProjectInput.removeProjectUser(userId);
      } else {
        createProjectInput.addProjectUser(userId);
      }
      setSearch('');
      setCurrentIndex(0);
      inputRef.current?.focus();
    },
    [createProjectInput]
  );

  //Handle create project
  const onclickInviteMember = useCallback(
    async (isSkip: boolean) => {
      if (!currentWorkspace?.id || !currentUser?.id) return;
      try {
        setButtonLoading(true);
        const payload: CreateProjectPayloadInterfaceV2 = {
          name: createProjectInput?.name,
          companyId: currentWorkspace?.id,
          startDate: createProjectInput?.startDate ? createProjectInput?.startDate.toISOString() : null,
          dueDate: createProjectInput?.dueDate ? createProjectInput?.dueDate.toISOString() : null,
          icon: {
            Type: createProjectInput?.icon?.type || PROJECT_ICON_TYPE.DEFAULT,
            Reference: createProjectInput?.icon?.reference || '',
            Color: createProjectInput?.icon?.color
          },
          projectUsers: !isEmpty(createProjectInput?.projectUsers) && !isSkip ? createProjectInput?.projectUsers : [],
          customerId: createProjectInput?.customerId || '',
          templateId: createProjectInput?.templateId || '',
          taskFilter: createProjectInput?.taskFilter
        };

        const result = await createNewProject(payload);
        if (result) {
          rootStore.projectStore.setCurrentProject(result?.data['_id']);
          trackAnalyticActivity(PROJECT_ANALYTICS.CREATED);
          history.push(ROUTES.PROJECT_TASKS.replace(':id', result?.data['_id'] || ''));
          dispatchProjectSuccess('Project created successfully');
        }
      } catch (error) {
        console.log('error', error);
      } finally {
        setButtonLoading(false);
      }
    },
    [
      createProjectInput?.customerId,
      createProjectInput?.dueDate,
      createProjectInput?.icon?.color,
      createProjectInput?.icon?.reference,
      createProjectInput?.icon?.type,
      createProjectInput?.name,
      createProjectInput?.projectUsers,
      createProjectInput?.startDate,
      createProjectInput?.taskFilter,
      createProjectInput?.templateId,
      currentUser?.id,
      currentWorkspace?.id,
      history
    ]
  );

  const searchedUsers = useMemo(() => {
    const workspaceUsers = currentWorkspace?.users?.filter(
      (user) => !user?.id?.startsWith('agent_') && user?.id !== currentUser?.id
    );
    const searchNewText = search?.trim()?.toLowerCase();
    const words = searchNewText?.split(' ').filter((word) => word.length > 0);
    const searchedUsers = workspaceUsers?.filter((item) => {
      const title = item?.name.trim().toLowerCase() || '';
      return words.some((word) => title.includes(word));
    });
    const users = isEmpty(search) ? workspaceUsers : searchedUsers;
    return users || [];
  }, [currentUser?.id, currentWorkspace?.users, search]);

  const workspaceUsersList = useMemo(() => {
    const list =
      searchedUsers?.map((user, index) => {
        return (
          <div ref={(el) => (itemRefs.current[index] = el)} key={user.id}>
            <DropdownItem
              isActive={!isMobile && currentIndex === index}
              onClick={() => handleProjectMember(user.id)}
              label={user.name}
              isAvatar
              avatarSrc={user.profile_photo || ''}
              isSuffix={
                createProjectInput?.users?.includes(user) ? (
                  <SVGIcon
                    name='check-icon'
                    width={ICON_SIZE.MD}
                    height={ICON_SIZE.MD}
                    viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                  />
                ) : null
              }
            />
          </div>
        );
      }) || [];

    if (isEmpty(list)) {
      return <NotFound>No member found</NotFound>;
    }
    return list;
  }, [createProjectInput?.users, currentIndex, handleProjectMember, isMobile, searchedUsers]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === 0 ? searchedUsers.length - 1 : prevIndex - 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          setIsDropdownOpen(true);
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === searchedUsers.length - 1 ? 0 : prevIndex + 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (searchedUsers[currentIndex]) {
            handleProjectMember(searchedUsers[currentIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setCurrentIndex(0);
          setIsDropdownOpen(false);
          setSearch('');
          break;
        default:
          break;
      }
    },
    [currentIndex, handleProjectMember, scrollToItem, searchedUsers]
  );

  // handle input change for members search
  const handleMembersSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentIndex(0);
    setSearch(e.target.value);
  }, []);

  // handle dropdown icon click to close
  const handleMembersIconClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isDropdownOpen) return;
      e.preventDefault();
      e.stopPropagation();
      membersDropdownRef.current?.closeDropdown();
    },
    [isDropdownOpen]
  );

  return (
    <>
      <OuterDiv>
        <LeftSection>
          <TopSection>
            <TitleSection>
              {!isMobile && <Heading>Team members</Heading>}
              <SubHeading>Invite member to {createProjectInput?.name} project.</SubHeading>
            </TitleSection>
            <Content>
              <Form>
                <Label>Select Members</Label>
                <Dropdown
                  ref={membersDropdownRef}
                  modalTitle='Members'
                  isMinWidth={398}
                  onOutsideClick={() => {
                    setCurrentIndex(0);
                  }}
                  updateDropdownOpen={setIsDropdownOpen}
                  closeOnItemClick={false}
                  content={
                    <Input
                      inputType='tags'
                      maxWidth='400px'
                      multiSelectProps={{
                        isActive: isDropdownOpen || isInputFocused,
                        selectedUsers:
                          createProjectInput?.users?.map((user) => ({
                            id: user.id,
                            displayName: user.name,
                            profile_photo: user.profile_photo
                          })) || [],
                        value: search,
                        placeholderWhenEmpty: 'Select members',
                        onChange: handleMembersSearchChange,
                        onKeyDown: handleKeyDown,
                        onFocus: () => setIsInputFocused(true),
                        onBlur: () => setIsInputFocused(false),
                        inputRef,
                        showIcon: true,
                        isDropdownOpen,
                        onIconClick: handleMembersIconClick,
                        onRemoveUser: (user) => handleProjectMember(user.id as string)
                      }}
                    />
                  }
                  trigger='click'>
                  <ItemDiv ref={dropdownRef}>{workspaceUsersList}</ItemDiv>
                </Dropdown>
              </Form>
            </Content>
          </TopSection>
          <ButtonSection>
            <Button
              title='Invite Teammates'
              variant={BUTTON_TYPE.PRIMARY}
              onClick={() => onclickInviteMember(false)}
              isLoading={buttonLoading}
            />
            <Button
              title='Skip'
              variant={BUTTON_TYPE.TERTIARY}
              size='medium'
              onClick={() => onclickInviteMember(true)}
            />
          </ButtonSection>
        </LeftSection>
      </OuterDiv>
    </>
  );
}

export default observer(TeamMembers);
